import {
  Autocomplete,
  Checkbox,
  Flex,
  GridCol,
  Loader,
  Select,
  TextInput,
  Tooltip,
} from "@mantine/core";
import Acknowledgement from "../Acknowledgement";
import { MSG } from "constants/msg";
import { FaQuestionCircle } from "react-icons/fa";
import { useFirmwareDeviceCodes } from "hooks/useDeviceCode";
import { useFirewareVersions } from "hooks/useFirewareVersion";
import { useOemModelIds } from "hooks/useOemModelId";
import { useEffect } from "react";
import _ from "lodash";
import {
  DntInfo,
  FormFileFieldLabels,
  FormFileValues,
} from "types/module/Firmware";
import { UseFormReturnType } from "@mantine/form";

const FormReadOnly = ({
  fieldLabels,
  form,
  isNotDnt,
  dntInfo,
}: {
  fieldLabels: FormFileFieldLabels;
  form: UseFormReturnType<FormFileValues>;
  isNotDnt: boolean;
  dntInfo: DntInfo;
}) => {
  const { data: deviceCodeData, isFetching: isDeviceCodeDataLoading } =
    useFirmwareDeviceCodes();

  const {
    data: firewareVersionData,
    isFetching: isFirewareVersionDataLoading,
    refetch: firewareVersionRefetch,
  } = useFirewareVersions(
    _.isEmpty(form?.values?.deviceCode)
      ? null
      : JSON.parse(form.values.deviceCode)[0]
  );
  const {
    data: oemModelIdData,
    isFetching: isOemModelIdDataLoading,
    refetch: oemModelIdDataRefetch,
  } = useOemModelIds(
    _.isEmpty(form?.values?.deviceCode)
      ? null
      : JSON.parse(form.values.deviceCode)[0]
  );

  useEffect(() => {
    if (
      !_.isEmpty(form?.values?.deviceCode) &&
      JSON.parse(form.values.deviceCode).length === 2
    ) {
      oemModelIdDataRefetch();
      firewareVersionRefetch();
    }
  }, [form.values.deviceCode, firewareVersionRefetch, oemModelIdDataRefetch]);

  return (
    <>
      {!isNotDnt && (
        <>
          <GridCol span={{ base: 12, md: 6 }}>
            <TextInput
              label={fieldLabels.firmwareFile}
              value={dntInfo.dntFirmwareFile}
              readOnly
            />
          </GridCol>
          <GridCol span={{ base: 12, md: 6 }}>
            <TextInput
              label={fieldLabels.danteProductFamily}
              value={dntInfo.product}
              readOnly
            />
          </GridCol>
          <GridCol span={{ base: 12, md: 6 }}>
            <TextInput
              label={fieldLabels.danteFirmware}
              value={dntInfo.firmwareVersion}
              readOnly
            />
          </GridCol>
          <GridCol span={{ base: 12, md: 6 }}>
            <TextInput
              label={fieldLabels.modelName}
              value={dntInfo.modelName}
              readOnly
            />
          </GridCol>
          <GridCol span={{ base: 12, md: 6 }}>
            <TextInput
              label={fieldLabels.modelVersion}
              value={dntInfo.productVersion}
              readOnly
            />
          </GridCol>
        </>
      )}
      {isNotDnt && (
        <>
          <GridCol span={{ base: 12, md: 6 }}>
            <TextInput
              label={fieldLabels.firmwareFile}
              value={dntInfo.dntFirmwareFile}
              readOnly
            />
          </GridCol>
          <GridCol span={{ base: 12, md: 6 }}>
            <Select
              key={form.key("deviceCode")}
              label={fieldLabels.deviceCode}
              placeholder={`Select ${fieldLabels.deviceCode}`}
              data={
                deviceCodeData &&
                deviceCodeData.map((item: any) => ({
                  label: item.name,
                  value: JSON.stringify([item.deviceCode, item.name]),
                }))
              }
              rightSection={isDeviceCodeDataLoading && <Loader size={15} />}
              {...form.getInputProps("deviceCode")}
            />
          </GridCol>
          <GridCol span={{ base: 12, md: 6 }}>
            <Select
              key={form.key("danteFirmware")}
              label={fieldLabels.danteFirmware}
              placeholder={`Select ${fieldLabels.danteFirmware}`}
              data={
                form.values.deviceCode &&
                firewareVersionData &&
                firewareVersionData.map((item: any) => ({
                  label: item,
                  value: item,
                }))
              }
              rightSection={
                isFirewareVersionDataLoading && <Loader size={15} />
              }
              {...form.getInputProps("danteFirmware")}
            />
          </GridCol>
          <GridCol span={{ base: 12, md: 6 }}>
            <TextInput
              key={form.key("modelName")}
              label={
                <Tooltip multiline w={400} label={MSG.HELP.PRODUCT_MODEL_NAME}>
                  <Flex justify="space-between" align="center" gap="xs" mt="0">
                    Product Model Name
                    <FaQuestionCircle />
                  </Flex>
                </Tooltip>
              }
              placeholder={`Enter ${fieldLabels.modelName}`}
              {...form.getInputProps("modelName")}
            />
          </GridCol>
          <GridCol span={{ base: 12, md: 6 }}>
            <Autocomplete
              key={form.key("modelId")}
              label={fieldLabels.modelId}
              placeholder={`Enter ${fieldLabels.modelId}`}
              data={
                form.values.deviceCode &&
                oemModelIdData &&
                oemModelIdData.map((item: any) => ({
                  label: item,
                  value: item,
                }))
              }
              rightSection={isOemModelIdDataLoading && <Loader size={15} />}
              {...form.getInputProps("modelId")}
            />
          </GridCol>
          <GridCol span={{ base: 12, md: 6 }}>
            <TextInput
              key={form.key("modelVersion")}
              label={fieldLabels.modelVersion}
              placeholder={`Select ${fieldLabels.modelVersion}`}
              {...form.getInputProps("modelVersion")}
            />
          </GridCol>
        </>
      )}
      <GridCol span={12}>
        <Acknowledgement>
          <Checkbox
            key={form.key("agree")}
            label="I agree"
            value={1}
            {...form.getInputProps("agree")}
            mt="md"
          />
        </Acknowledgement>
      </GridCol>
    </>
  );
};

export default FormReadOnly;
